import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('bidsentry_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth Context
const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('bidsentry_token');
    const savedUser = localStorage.getItem('bidsentry_user');
    
    if (token && savedUser) {
      setUser(JSON.parse(savedUser));
      // Verify token is still valid
      api.get('/auth/me')
        .then(res => {
          setUser(res.data);
          localStorage.setItem('bidsentry_user', JSON.stringify(res.data));
        })
        .catch(() => {
          logout();
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email, password) => {
    const res = await api.post('/auth/login', { email, password });
    const { access_token, user: userData } = res.data;
    
    localStorage.setItem('bidsentry_token', access_token);
    localStorage.setItem('bidsentry_user', JSON.stringify(userData));
    setUser(userData);
    try { window.posthog?.identify(userData.id); } catch (_) { /* silent */ }
    
    return userData;
  };

  const register = async (email, password, name, consentAccepted) => {
    const res = await api.post('/auth/register', { email, password, name, consent_accepted: consentAccepted });
    const { access_token, user: userData } = res.data;
    
    localStorage.setItem('bidsentry_token', access_token);
    localStorage.setItem('bidsentry_user', JSON.stringify(userData));
    setUser(userData);
    try { window.posthog?.identify(userData.id); } catch (_) { /* silent */ }
    
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('bidsentry_token');
    localStorage.removeItem('bidsentry_user');
    setUser(null);
  };

  const updateUser = (userData) => {
    setUser(userData);
    localStorage.setItem('bidsentry_user', JSON.stringify(userData));
  };

  const updateToken = (token) => {
    localStorage.setItem('bidsentry_token', token);
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      login, 
      register, 
      logout, 
      updateUser,
      updateToken,
      isAuthenticated: !!user 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// API hooks and functions
export { api, API, BACKEND_URL };
